import AppContext from 'App/AppContext';
import { IPFontIcon, IPLabel, IPScrollablePane, IPText } from 'baseComponents';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import {
  globalStackItemStylesPaddingSceneScroll,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
} from 'globalStyles';
import { ApprovalState } from 'models/approval';
import Tag from 'models/tag';
import Task from 'models/tasks/task';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { toLocaleDateTimeMedium } from 'utils/datetime';
import { TaskDetailName } from '../Details/TaskDetailName';
import { TaskDetailStart } from '../Details/TaskDetailStart';
import { TaskDetailTags } from '../Details/TaskDetailTags';
import { ProcessingActivityApprovalTaskDetailItems } from './ProcessingActivityApprovalTaskDetailItems';
import TaskTypeForm from 'components/Tasks/Forms/TaskTypeForm';
import { hasUserRolePermission } from 'services/Auth/featurePermissions';
import { PermissionTypes } from 'models/auth/rolePermission';
import KPIData from 'models/kpi/kpiData';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';

interface IProcessingActivityApprovalTaskDetail {
  task: Task;
  masterTask: Task;
  orgTask: Task;
  getErrorMessage: (field: string) => string | undefined;
  onUpdate: (newTask: Task) => void;
  isActionPending: boolean;
  tags: Tag[];
  addTagToTaskState: (tag: Tag) => void;
  removeTagFromTaskState: (tag: Tag) => void;
  onChangeCompletionDate: (completionDate: Date) => void;
  windowLevel?: number;
  navigateExternal?: boolean;
  canUpdate: boolean;
  onUpdateKPIData: (data: KPIData) => void;
  onSetKPIData: (data: KPIData | KPIData[], instances?: Task[] | undefined) => void;
  onUpdateTaskForForm: (updatedTask: Task, validateForm: boolean) => void;
  showFormErrors: string | undefined;
  onValidateForm: (errors: TaskTypeFormElement[]) => void;
}

export const ProcessingActivityApprovalTaskDetail = (props: IProcessingActivityApprovalTaskDetail) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['task', 'translation', 'library']);
  const iconFontSize = { fontSize: '20px', marginTop: '5px' };

  const isApprovedOrRejected = (): boolean => {
    return props.task.approved !== ApprovalState.Pending;
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPStack verticalFill tokens={globalStackTokensGapSmall} styles={{ root: { paddingRight: 10 } }}>
            <TaskDetailName
              orgTask={props.orgTask}
              task={props.task}
              canUpdate={props.canUpdate}
              onUpdate={props.onUpdate}
              getErrorMessage={props.getErrorMessage}
            />
            <TaskDetailTags
              orgTask={props.orgTask}
              task={props.task}
              tags={props.tags}
              canUpdate={props.canUpdate}
              addTagToTaskState={props.addTagToTaskState}
              removeTagFromTaskState={props.removeTagFromTaskState}
              isActionPending={props.isActionPending}
            />
            {hasUserRolePermission(appContext, PermissionTypes.ApproveProcessingActivity) && (
              <ProcessingActivityApprovalTaskDetailItems
                task={props.task}
                onUpdate={props.onUpdate}
                navigateExternal={props.navigateExternal}
              />
            )}
            <TaskDetailStart
              orgTask={props.orgTask}
              task={props.task}
              canUpdate={props.canUpdate && !isApprovedOrRejected()}
              onUpdate={props.onUpdate}
              getErrorMessage={props.getErrorMessage}
            />
            <IPStack
              horizontal
              tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
              verticalAlign="start"
            >
              <IPStack.Item>
                {!appContext.isMobileView && <IPFontIcon iconName="InfoSolid" style={iconFontSize} />}
              </IPStack.Item>
              <IPStack.Item grow>
                <IPLabel>{t('task:Deadline.Label')}</IPLabel>
                <IPText>{toLocaleDateTimeMedium(props.task.deadline)}</IPText>
              </IPStack.Item>
            </IPStack>
            <IPStack.Item grow styles={appContext.isMobileView ? { root: { paddingLeft: 0 } } : { root: { paddingLeft: 28 } }}>
              <TaskTypeForm
                isOpen={true}
                task={props.masterTask}
                links={[]}
                compact={true}
                readonly={props.orgTask.isCompleted()} //the SAVED state decides: picking another status must not unlock the form
                onUpdateData={props.onUpdateKPIData}
                onSetData={props.onSetKPIData}
                onUpdateTaskForForm={props.onUpdateTaskForForm}
                addLinks={() => {}}
                setLinks={() =>{}}
                removeLink={() => {}}
                showErrors={props.showFormErrors}
                onValidate={props.onValidateForm}
                hideEmptyFormText={true}
              />
            </IPStack.Item>
          </IPStack>
        </IPScrollablePane>
      </IPStack.Item>
    </IPStack>
  );
};
